import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminPressKitService } from '../services/admin.pressKit.service'
import type { PressKitCoverageWithId, PressKitCoverageFormValues } from '@/features/press-kit/types'

interface UseAdminPressKitCoverageResult {
  coverage: PressKitCoverageWithId[]
  loading: boolean
  error: string | null
  createCoverage: (values: PressKitCoverageFormValues) => Promise<string>
  updateCoverage: (id: string, values: Partial<PressKitCoverageFormValues>) => Promise<void>
  updateThumbnail: (id: string, url: string) => Promise<void>
  updateCoverageOrder: (id: string, order: number) => Promise<void>
  deleteCoverage: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminPressKitCoverage(): UseAdminPressKitCoverageResult {
  const [coverage, setCoverage] = useState<PressKitCoverageWithId[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminPressKitService
      .getAllCoverage()
      .then(setCoverage)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load coverage'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createCoverage = useCallback(
    async (values: PressKitCoverageFormValues): Promise<string> => {
      const id = await adminPressKitService.createCoverage(values, coverage.length)
      refresh()
      return id
    },
    [coverage.length, refresh]
  )

  const updateCoverage = useCallback(
    async (id: string, values: Partial<PressKitCoverageFormValues>): Promise<void> => {
      await adminPressKitService.updateCoverage(id, values)
      refresh()
    },
    [refresh]
  )

  const updateThumbnail = useCallback(async (id: string, url: string): Promise<void> => {
    await adminPressKitService.updateCoverageThumbnail(id, url)
    refresh()
  }, [refresh])

  const updateCoverageOrder = useCallback(async (id: string, order: number): Promise<void> => {
    await adminPressKitService.updateCoverageOrder(id, order)
    refresh()
  }, [refresh])

  const deleteCoverage = useCallback(async (id: string): Promise<void> => {
    await adminPressKitService.deleteCoverage(id)
    refresh()
  }, [refresh])

  return {
    coverage,
    loading,
    error,
    createCoverage,
    updateCoverage,
    updateThumbnail,
    updateCoverageOrder,
    deleteCoverage,
    refresh,
  }
}
